[[]]
<template>
  <div v-if="topic.feedback && canFeedbackOnThoughts(topic)" ref="feedbackFormContainer">
    <!-- <div class="alert alert-info" v-if="infotext !== ''">{{ infotext }}</div> -->
    <!-- <div class="alert alert-danger" v-if="serverErr !== ''">{{ serverErr }}</div> -->
    <ValidationForm @submit="onSubmitFeedbackResponseForm" v-if="formReady">
      <div id="wdw-feedback-response-form" class="wdw-form">
        <form-control-attributes
          elementPrefixId="wdw-discuss-thought-form"
          v-on:validate="onValidatedFormField"
          :requiredIfResults="requiredIfResult"
          :visibleIfResults="visibleIfResult"
          :fields="feedbackAttributes"
          :serverErrors="feedbackState.errors"
          :initialValues="attributeEditInitialValues"
        />

        <button type="submit" :disabled="feedbackState.submitting" class="btn btn-primary btn-form-submit" name="save">
          {{ domainStore.translate('discuss.feedback.form.submit') }}
        </button>
        &nbsp;
        <button type="button" class="btn btn-default btn-form-cancel" @click="emitCloseForm()">
          {{ domainStore.translate('discuss.feedback.form.cancel') }}
        </button>
        <vue-loading v-if="feedbackState.submitting" />
      </div>
    </ValidationForm>
    <div v-else>
      <vue-loading :loadingMessage="domainStore.translate('discuss.feedback.message.loading-form')" />
    </div>
  </div>
  <div v-else-if="canFeedbackAfterLogin(topic)" class="alert alert-info">
    {{ domainStore.translate('discuss.feedback.message.requires-login') }}
  </div>
</template>
<script>
import { computed, onMounted, nextTick } from '@vue/runtime-core';
import { ref, toRefs } from '@vue/reactivity';

import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';

import VueLoading from '@/components/general/Loading';
import FormControlAttributes from '@/components/general/FormControl/Attributes';
import { Form as ValidationForm } from 'vee-validate';

import useTopicConfig from '@/composables/topic/useTopicConfig';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import useConverter from '@/composables/general/useConverter';
import useFormInputDependency from '@/composables/general/useFormInputDependency';
import useThoughtFeedbackState from '@/composables/thought/useThoughtFeedbackState';

import { RepositoryFactory } from '@/api/repositoryFactory';
const FeedbackRepository = RepositoryFactory.get('feedback');

// TODO
// [] -> get topic using topicid

export default {
  name: 'FeedbackResponse',

  props: {
    thought: {
      type: Object,
      required: true,
    },
    topic: {
      required: true,
      type: Object,
    },
  },

  emits: ['feedback-form-close'],

  components: {
    VueLoading,
    FormControlAttributes,
    ValidationForm,
  },

  setup(props, context) {
    const { thought, topic } = toRefs(props);
    const {
      canFeedbackOnThoughts,
      canFeedbackAfterLogin,
      // canEditFeedback,
    } = useTopicConfig();
    const feedback_response = ref();
    const { toBool } = useConverter();
    const { evaluateFormIfCode } = useFormInputDependency();
    const { l } = useLocalizeMessages();
    const { visibleIfResult, requiredIfResult, feedbackAttributes, feedbackState, submitFeedbackResponse } =
      useThoughtFeedbackState(thought.value.id);
    const feedbackFormContainer = ref(HTMLElement | null);

    const user = computed(() => {
      return userStore.getState().user;
    });
    const formReady = computed(() => {
      return feedbackAttributes.value.length > 0 && !feedbackState.refreshing && !feedbackState.sbumitting;
    });
    const attributeEditInitialValues = computed(() => {
      /**
       * For editing, we take the existing values from feedback_response and
       * apply it as default value of the attribute form controls
       */
      const existingValues = feedbackState.response ? feedbackState.response.attributes : null;
      if (existingValues == null) {
        return {};
      } else {
        const defValues = {};
        const fdbkAttributes = feedbackAttributes.value;
        existingValues.forEach(x => {
          const curAttr = fdbkAttributes.filter(a => a.id == x.id);
          if (curAttr.length > 0) {
            if (curAttr[0].type === 'valuelist' || curAttr[0].type === 'select') {
              defValues['attribute-' + x.id] = x.value_all;
            } else if (curAttr[0].type === 'checkbox') {
              defValues['attribute-' + x.id] = toBool(x.value);
            } else {
              defValues['attribute-' + x.id] = x.value;
            }
          } else {
            defValues['attribute-' + x.id] = x.value;
          }
        });
        return defValues;
      }
    });

    function emitCloseForm(data) {
      context.emit('feedback-form-close', data);
    }
    const onSubmitFeedbackResponseForm = (values, actions) => {
      submitFeedbackResponse(values).then(resolved => {
        if (resolved) {
          emitCloseForm(feedbackState);
        }
      });
    };

    const onValidatedFormField = fieldValue => {
      // Evaluate the whole attribute fields based on its visible_if_code
      visibleIfResult.value = evaluateFormIfCode(
        'visible_if_code',
        'visible_if_value',
        visibleIfResult.value,
        feedbackAttributes.value,
        fieldValue.code,
        fieldValue.value,
        fieldValue.type
      );
      // Evaluate the whole attribute fields based on its required_if_code
      requiredIfResult.value = evaluateFormIfCode(
        'required_if_code',
        'required_if_value',
        requiredIfResult.value,
        feedbackAttributes.value,
        fieldValue.code,
        fieldValue.value,
        fieldValue.type
      );
    };

    /**
     * +------------------------+
     * |    Lifecycle hook      |
     * +------------------------+
     */
    onMounted(() => {
      // Init requiredIfResults
      feedbackAttributes.value.forEach(attr => {
        requiredIfResult.value[attr.code] = toBool(attr.required);
      });

      // make sure Form is scrolled into view when it's opened
      if (feedbackFormContainer.value) {
        nextTick(function () {
          feedbackFormContainer.value.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
    });

    return {
      domainStore,
      // Methods
      canFeedbackOnThoughts,
      canFeedbackAfterLogin,
      onSubmitFeedbackResponseForm,
      onValidatedFormField,
      emitCloseForm,

      // Computed
      attributeEditInitialValues,
      formReady,

      // VARS
      // data,
      feedbackFormContainer,
      feedbackAttributes,
      feedbackState,
      submitFeedbackResponse,
      visibleIfResult,
      requiredIfResult,

      //DEV
      feedback_response,
    };
  },
};
</script>
